import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft, Check, RefreshCw, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { CredentialsRotateDialog } from '@/accounts/credentials-rotate-dialog';
import {
  useAccount,
  useAccountActiveStats,
  useAccountInventory,
  useDeleteAccount,
  useDestroyAccount,
  useRotateCredentials,
  useSessionHealth,
  useUpdateAccount,
} from '@/accounts/queries';
import { ACCOUNT_ROLES, type AccountRole } from '@/accounts/schemas';
import { humanErrorMessage } from '@/api/errors';
import { useAuthStore } from '@/auth/store';
import { ConfirmDeleteDialog } from '@/components/confirm-delete-dialog';
import { DestroyDialog } from '@/components/destroy-dialog';
import { PageShell } from '@/components/page-shell';
import { StatusDot } from '@/components/status-dot';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const formatDate = (iso: string): string => new Date(iso).toLocaleString('ru-RU');

// Phase 8.5-04 SEC-01 — `credentialsPresent` indicator. ADR-014. Replaces the
// pre-Phase-8.5 masked-secret rows (those leaked the *shape* of stored blobs;
// REQ SEC-01 mandates removal).
const PresenceIcon = ({ present }: { present: boolean }) =>
  present ? (
    <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
      <Check className="h-3.5 w-3.5" /> есть
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
      <X className="h-3.5 w-3.5" /> нет
    </span>
  );

export const AccountDetailPage = () => {
  const { accountId } = useParams({ from: '/_protected/accounts/$accountId' });
  const navigate = useNavigate();
  const { data: account, isLoading } = useAccount(accountId);
  const { data: health } = useSessionHealth(accountId);
  const activeStats = useAccountActiveStats(accountId);
  const updateAccount = useUpdateAccount(accountId);
  const deleteAccount = useDeleteAccount();
  const destroyAccount = useDestroyAccount();
  const rotateCredentials = useRotateCredentials();
  const role = useAuthStore((s) => s.currentUser?.role);
  const isAdmin = role === 'admin';
  const isOperatorOrAdmin = role === 'admin' || role === 'operator';
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [destroyOpen, setDestroyOpen] = useState(false);
  const [rotateOpen, setRotateOpen] = useState(false);
  const [rotateError, setRotateError] = useState<string | undefined>(undefined);
  const [maxActiveDraft, setMaxActiveDraft] = useState<string>('');
  // Plan 10-03 — operators don't always need to see live Steam inventory
  // (network-bound), so the accordion is collapsed by default. The query
  // is gated by `inventoryOpen` and disabled for non-storage accounts.
  const [inventoryOpen, setInventoryOpen] = useState(false);
  const inventory = useAccountInventory(accountId, {
    enabled: inventoryOpen && account?.role === 'storage',
  });

  if (isLoading) return <div className="p-6 text-muted-foreground">Загрузка…</div>;
  if (!account) return <div className="p-6 text-muted-foreground">Аккаунт не найден.</div>;

  const isStorage = account.role === 'storage';
  const remaining = activeStats.data
    ? Math.max(0, activeStats.data.maxActiveTrades - activeStats.data.activeTrades)
    : null;
  const limitPct = activeStats.data
    ? Math.min(
        100,
        Math.round((activeStats.data.activeTrades / activeStats.data.maxActiveTrades) * 100),
      )
    : 0;

  // Phase 8.5-04 UX-01 — tier-1 soft-delete; on tier-2 trigger (active trades),
  // server returns 409 → UI auto-opens DestroyDialog with steamLogin
  // typed-confirmation. ADR-014.
  const handleDelete = (): void => {
    deleteAccount.mutate(account.id, {
      onSuccess: () => {
        toast.success('Аккаунт удалён');
        setConfirmDeleteOpen(false);
        void navigate({ to: '/accounts' });
      },
      onError: (err) => {
        const msg = humanErrorMessage(err);
        if (msg.includes('account_has_active_trades')) {
          setConfirmDeleteOpen(false);
          setDestroyOpen(true);
          toast.message('Аккаунт имеет активные trades — нужен tier-3 destroy с подтверждением.');
          return;
        }
        toast.error(msg);
      },
    });
  };

  const handleDestroy = (): void => {
    destroyAccount.mutate(
      { id: account.id, confirmation: account.steamLogin },
      {
        onSuccess: () => {
          toast.success('Аккаунт уничтожен');
          setDestroyOpen(false);
          void navigate({ to: '/accounts' });
        },
        onError: (err) => toast.error(humanErrorMessage(err)),
      },
    );
  };

  const handleRotate = (
    payload: Parameters<typeof rotateCredentials.mutate>[0]['payload'],
  ): void => {
    setRotateError(undefined);
    rotateCredentials.mutate(
      { id: account.id, payload },
      {
        onSuccess: (res) => {
          toast.success(
            res.rotatedFields.length > 0
              ? `Ротировано: ${res.rotatedFields.join(', ')}`
              : 'Ротация завершена',
          );
          setRotateOpen(false);
        },
        onError: (err) => {
          setRotateError(humanErrorMessage(err));
        },
      },
    );
  };

  const isDeleted = Boolean(account.deletedAt);

  return (
    <div className="space-y-4 p-6">
      <Link
        to="/accounts"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Аккаунты
      </Link>
      <PageShell
        title={account.steamLogin}
        description={account.steamId64}
        actions={
          isDeleted ? (
            <Badge variant="destructive">Удалён</Badge>
          ) : (
            <Button
              variant="destructive"
              onClick={() => {
                setConfirmDeleteOpen(true);
              }}
            >
              Удалить
            </Button>
          )
        }
      >
        <dl className="grid grid-cols-[200px_1fr] gap-y-3 p-6 text-sm">
          <dt className="text-muted-foreground">Статус</dt>
          <dd>
            <StatusDot domain="account" state={account.status} />
          </dd>
          <dt className="text-muted-foreground">Роль</dt>
          <dd className="flex items-center gap-3">
            {isOperatorOrAdmin && !isDeleted ? (
              <div className="w-40">
                <Select
                  value={account.role}
                  onValueChange={(v) => {
                    const next = v as AccountRole;
                    if (next === account.role) return;
                    updateAccount.mutate(
                      { role: next },
                      {
                        onSuccess: () =>
                          toast.success(`Роль: ${next === 'storage' ? 'Склад' : 'Бот'}`),
                        onError: (err) => toast.error(humanErrorMessage(err)),
                      },
                    );
                  }}
                  disabled={updateAccount.isPending}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACCOUNT_ROLES.map((r) => (
                      <SelectItem key={r} value={r}>
                        {r === 'storage' ? 'Склад' : 'Бот'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <Badge variant={isStorage ? 'secondary' : 'default'}>
                {isStorage ? 'Склад' : 'Бот'}
              </Badge>
            )}
          </dd>
          <dt className="text-muted-foreground">Активных / лимит</dt>
          <dd className="flex items-center gap-3">
            {isOperatorOrAdmin && !isDeleted ? (
              <>
                <Input
                  className="w-24"
                  type="number"
                  min={1}
                  max={1000}
                  value={maxActiveDraft || String(account.maxActiveTrades)}
                  onChange={(e) => {
                    setMaxActiveDraft(e.target.value);
                  }}
                />
                <Button
                  size="sm"
                  variant="outline"
                  disabled={
                    updateAccount.isPending ||
                    maxActiveDraft === '' ||
                    Number(maxActiveDraft) === account.maxActiveTrades
                  }
                  onClick={() => {
                    const next = Number(maxActiveDraft);
                    if (!Number.isInteger(next) || next < 1 || next > 1000) {
                      toast.error('Лимит: целое число 1…1000');
                      return;
                    }
                    updateAccount.mutate(
                      { maxActiveTrades: next },
                      {
                        onSuccess: () => {
                          toast.success(`Лимит: ${String(next)}`);
                          setMaxActiveDraft('');
                        },
                        onError: (err) => toast.error(humanErrorMessage(err)),
                      },
                    );
                  }}
                >
                  Сохранить
                </Button>
              </>
            ) : (
              <span>
                {account.activeTrades}/{account.maxActiveTrades}
              </span>
            )}
          </dd>
          <dt className="text-muted-foreground">В работе</dt>
          <dd>
            {activeStats.isPending ? (
              <span className="text-muted-foreground">…</span>
            ) : activeStats.isError ? (
              <span className="text-destructive">не удалось загрузить</span>
            ) : (
              <div className="flex items-center gap-3">
                <span className="text-sm">
                  {activeStats.data.activeTrades}/{activeStats.data.maxActiveTrades}
                  {remaining !== null ? ` (осталось ${String(remaining)})` : ''}
                </span>
                <div
                  className="h-2 w-32 overflow-hidden rounded bg-muted"
                  title={`Заполнено ${String(limitPct)}%`}
                >
                  <div
                    className={`h-full ${limitPct >= 100 ? 'bg-destructive' : 'bg-primary'}`}
                    style={{ width: `${String(limitPct)}%` }}
                  />
                </div>
              </div>
            )}
          </dd>
          <dt className="text-muted-foreground">Email</dt>
          <dd>{account.email ?? '—'}</dd>
          <dt className="text-muted-foreground">Доступен после</dt>
          <dd>{formatDate(account.availableAfter)}</dd>
          <dt className="text-muted-foreground">Учётные данные</dt>
          <dd className="flex items-center gap-3">
            <PresenceIcon present={account.credentialsPresent} />
            {isAdmin && !isDeleted ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setRotateError(undefined);
                  setRotateOpen(true);
                }}
              >
                Ротировать…
              </Button>
            ) : (
              <span className="text-xs text-muted-foreground">
                (admin-only — POST /accounts/:id/credentials/rotate)
              </span>
            )}
          </dd>
          <dt className="text-muted-foreground">Refresh token</dt>
          <dd>
            <PresenceIcon present={account.hasRefreshToken} />
          </dd>
          <dt className="text-muted-foreground">Пароль почты</dt>
          <dd>
            <PresenceIcon present={account.hasEmailPassword} />
          </dd>
          <dt className="text-muted-foreground">Последнее обновление</dt>
          <dd>{account.lastRefreshAt ? formatDate(account.lastRefreshAt) : '—'}</dd>
          {health ? (
            <>
              <dt className="text-muted-foreground">Состояние сессии</dt>
              <dd>
                <Badge variant={health.alive ? 'default' : 'destructive'}>
                  {health.alive ? 'alive' : 'dead'}
                </Badge>
                {health.stale ? (
                  <Badge variant="warning" className="ml-2">
                    stale ({String(health.thresholdDays)}d)
                  </Badge>
                ) : null}
              </dd>
            </>
          ) : null}
          <dt className="text-muted-foreground">Куплен</dt>
          <dd>{account.purchasedAt ? formatDate(account.purchasedAt) : '—'}</dd>
          <dt className="text-muted-foreground">Создан</dt>
          <dd>{formatDate(account.createdAt)}</dd>
        </dl>
      </PageShell>

      {isStorage ? (
        <PageShell title="Инвентарь склада (Steam, кэш 60s)">
          <div className="space-y-3 p-6">
            <div className="flex items-center gap-3">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  if (!inventoryOpen) {
                    setInventoryOpen(true);
                  } else {
                    void inventory.refetch();
                  }
                }}
                disabled={inventory.isFetching}
              >
                <RefreshCw className="mr-1 h-3.5 w-3.5" />
                {inventoryOpen ? 'Обновить' : 'Загрузить'}
              </Button>
              {inventoryOpen && inventory.data ? (
                <span className="text-xs text-muted-foreground">
                  {`appid ${String(inventory.data.appid)} / ctx ${String(inventory.data.contextId)} · кэшировано ${new Date(
                    inventory.data.cachedAt,
                  ).toLocaleString('ru-RU')}`}
                </span>
              ) : null}
            </div>
            {!inventoryOpen ? (
              <p className="text-sm text-muted-foreground">
                Сетевой запрос — нажмите «Загрузить» когда понадобится.
              </p>
            ) : inventory.isPending ? (
              <p className="text-sm text-muted-foreground">Загрузка инвентаря…</p>
            ) : inventory.isError ? (
              <p className="text-sm text-destructive">
                Не удалось получить инвентарь: {humanErrorMessage(inventory.error)}
              </p>
            ) : inventory.data.items.length === 0 ? (
              <p className="text-sm text-muted-foreground">Инвентарь пуст.</p>
            ) : (
              <ul className="divide-y rounded border text-sm">
                {inventory.data.items.map((item) => (
                  <li
                    key={item.marketHashName}
                    className="flex items-center justify-between gap-3 px-3 py-2"
                  >
                    <span className="truncate">{item.marketHashName}</span>
                    <span className="flex items-center gap-2">
                      <Badge variant="outline">×{String(item.count)}</Badge>
                      <span className="text-xs text-muted-foreground">tradable</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </PageShell>
      ) : null}

      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title="Удалить аккаунт?"
        description={`Аккаунт «${account.steamLogin}» будет soft-deleted (если нет активных trades) или вы будете перенаправлены на typed-confirmation для tier-3 destroy.`}
        onConfirm={handleDelete}
        isPending={deleteAccount.isPending}
      />

      <DestroyDialog
        open={destroyOpen}
        onOpenChange={setDestroyOpen}
        title="Уничтожить аккаунт (tier-3, irreversible)?"
        description={`Аккаунт «${account.steamLogin}» будет hard-deleted. Trade-history сохраняется (account_id обнуляется), proxy-привязки удаляются. Действие необратимо. Введите steamLogin для подтверждения.`}
        resourceLabel="steamLogin"
        expectedConfirmation={account.steamLogin}
        onConfirm={handleDestroy}
        isPending={destroyAccount.isPending}
      />

      <CredentialsRotateDialog
        open={rotateOpen}
        onOpenChange={(next) => {
          setRotateOpen(next);
          if (!next) setRotateError(undefined);
        }}
        steamLogin={account.steamLogin}
        isPending={rotateCredentials.isPending}
        {...(rotateError !== undefined ? { submitError: rotateError } : {})}
        onSubmit={handleRotate}
      />
    </div>
  );
};
